import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { toast } from "react-toastify";
import {
  exportVenues,
  formatApiDate,
  formatDisplayDateTime,
  getVenues,
  getVenueStats,
  locationFilterParams,
} from "../../api/superAdminApi";
import { VM_venueTypes } from "../../Helper/RegistrationHelper";
import { useLocationCascading } from "./hooks/useLocationCascading";
import { useAdminPanel } from "./hooks/useAdminPanel";
import SuperAdminLocationSelects from "./SuperAdminLocationSelects";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import SuperAdminViewAction from "./SuperAdminViewAction";
import {
  getUrlFilterOverrides,
  hasSubscriptionListFilter,
  hasUrlListFilters,
  shouldApplyClientListFilter,
} from "./utils/dashboardNavigation";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const VENUE_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const SUBSCRIPTION_OPTIONS = [
  { value: "", label: "All" },
  { value: "yes", label: "Has subscription" },
  { value: "no", label: "No subscription" },
  { value: "active", label: "Active plan" },
  { value: "expired", label: "Expired subscription" },
];

const PROFILE_OPTIONS = [
  { value: "", label: "All" },
  { value: "trial", label: "Trial" },
  { value: "free", label: "Free" },
  { value: "premium", label: "Premium" },
];

const VENUE_TYPE_OPTIONS = [
  { value: "", label: "All" },
  ...VM_venueTypes.map((v) => ({ value: v.value, label: v.label })),
];

const TABLE_HEADERS = [
  "S.No",
  "Venue Manager",
  "Contact",
  "Venue Name",
  "Profile",
  "Status",
  "Subscription",
  "City",
  "Created",
  "Action",
];

const EMPTY_LIST_FILTERS = {
  status: "",
  venue_status: "",
  subscription: "",
  profile: "",
  venue_type: "",
  subscription_expiry: "",
  subscription_purchased_today: "",
};

const STAT_CARDS = [
  { key: "total", label: "Total Venues", filter: { ...EMPTY_LIST_FILTERS } },
  { key: "active_count", label: "Active Managers", filter: { ...EMPTY_LIST_FILTERS, status: "active" } },
  { key: "inactive_count", label: "Inactive Managers", filter: { ...EMPTY_LIST_FILTERS, status: "inactive" } },
  { key: "active_venue_count", label: "Active Venues", filter: { ...EMPTY_LIST_FILTERS, venue_status: "active" } },
  { key: "inactive_venue_count", label: "Inactive Venues", filter: { ...EMPTY_LIST_FILTERS, venue_status: "inactive" } },
  {
    key: "with_subscription",
    label: "With Subscription",
    filter: { ...EMPTY_LIST_FILTERS, subscription: "yes" },
  },
  {
    key: "without_subscription",
    label: "Without Subscription",
    filter: { ...EMPTY_LIST_FILTERS, subscription: "no" },
  },
  {
    key: "active_subscription",
    label: "Active Subscription",
    filter: { ...EMPTY_LIST_FILTERS, subscription: "active" },
  },
];

const PROFILE_CARD_KEYS = ["Premium", "Trial", "Free"];

const cardPaperSx = (selected) => ({
  p: 2,
  textAlign: "center",
  cursor: "pointer",
  transition: "box-shadow 0.2s, transform 0.15s",
  "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
  ...(selected ? { outline: "3px solid", outlineColor: "common.white", boxShadow: 6 } : {}),
});

function getTableTitle(selectedCard, typeItems) {
  const statMatch = STAT_CARDS.find((c) => c.key === selectedCard);
  if (statMatch && selectedCard !== "total") return statMatch.label;

  if (PROFILE_CARD_KEYS.includes(selectedCard)) return selectedCard;

  if (selectedCard === "subscription_purchased_today") return "Today Subscriptions";
  if (selectedCard?.startsWith("expiry:")) {
    const period = selectedCard.replace("expiry:", "").replace(/_/g, " ");
    return `Subscription expiry — ${period}`;
  }

  const typeMatch = typeItems.find((t) => `type:${t.venue_type}` === selectedCard);
  if (typeMatch) return typeMatch.venue_type;

  return "All Venues";
}

function applySubscriptionParams(params, subscription) {
  if (subscription === "yes" || subscription === "no") {
    params.has_subscription = subscription;
  } else if (subscription === "active" || subscription === "expired") {
    params.subscription = subscription;
  }
}

function matchesListFilters(row, filters = {}) {
  if (filters.status) {
    const rowStatus = String(row.status || row.account_status || "").toLowerCase();
    if (filters.status === "active" && rowStatus !== "active") return false;
    if (filters.status === "inactive" && rowStatus === "active") return false;
  }
  if (filters.venue_status) {
    const isActive = row.is_venue_active ?? row.venue_status === true;
    if (filters.venue_status === "active" && !isActive) return false;
    if (filters.venue_status === "inactive" && isActive) return false;
  }
  if (filters.profile) {
    if (String(row.profile || "").toLowerCase() !== String(filters.profile).toLowerCase()) return false;
  }
  if (filters.venue_type) {
    if (String(row.venue_type || "").toLowerCase() !== String(filters.venue_type).toLowerCase()) return false;
  }
  if (filters.subscription === "yes" && !row.has_subscription) return false;
  if (filters.subscription === "no" && row.has_subscription) return false;
  if (filters.subscription === "active" && !row.is_subscription_active) return false;
  return true;
}

function profileChipColor(profile) {
  if (profile === "Premium") return "success";
  if (profile === "Trial") return "warning";
  return "default";
}

function ProfileBadge({ profile }) {
  if (!profile) return "—";
  return <Chip label={profile} size="small" color={profileChipColor(profile)} />;
}

function StatusBadge({ status }) {
  if (!status) return "—";
  const isActive = String(status).toLowerCase() === "active";
  return (
    <Chip
      label={status}
      size="small"
      color={isActive ? "success" : "default"}
      variant={isActive ? "filled" : "outlined"}
    />
  );
}

function SubscriptionIndicator({ hasSubscription, isActive }) {
  const subscribed = hasSubscription === true || hasSubscription === "true";
  const active = isActive === true || isActive === "true";
  const label = !subscribed ? "No subscription" : active ? "Paid — Active" : "Subscription — Inactive";
  return (
    <Chip
      label={label}
      size="small"
      color={!subscribed ? "default" : active ? "success" : "warning"}
      variant={active ? "filled" : "outlined"}
    />
  );
}

const FILTER_ITEM = { xs: 12, sm: 6, md: 4, lg: 3 };
const filterFormControlSx = { minWidth: { xs: "100%", sm: 220 }, width: "100%" };
const filterMenuProps = {
  PaperProps: { sx: { maxHeight: 320, minWidth: 240, mt: 0.5 } },
};

function FilterSelect({ label, labelId, value, onChange, options }) {
  return (
    <FormControl fullWidth size="small" sx={filterFormControlSx}>
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={onChange}
        displayEmpty
        notched
        MenuProps={filterMenuProps}
      >
        {options.map((o) => (
          <MenuItem key={o.value || "all"} value={o.value}>
            {o.value ? o.label : <em>{o.label}</em>}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function FilterSectionTitle({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", mt: 0.5 }}>
      {children}
    </Typography>
  );
}

export default function VMSuperAdminVenues() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminScope, locationLocked } = useAdminPanel();
  const loc = useLocationCascading();

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [status, setStatus] = useState("");
  const [venueStatus, setVenueStatus] = useState("");
  const [subscription, setSubscription] = useState("");
  const [profile, setProfile] = useState("");
  const [venueType, setVenueType] = useState("");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState("total");

  const listRequestSeq = useRef(0);
  const activeListFiltersRef = useRef({ ...EMPTY_LIST_FILTERS });
  const persistedLocationRef = useRef(null);

  const buildBaseFilters = useCallback(
    (overrides = {}) => {
      const effectiveSearch = overrides.search !== undefined ? overrides.search : searchKey;
      const effectiveFromDate = overrides.fromDate !== undefined ? overrides.fromDate : fromDate;
      const effectiveToDate = overrides.toDate !== undefined ? overrides.toDate : toDate;
      const effectiveLoc = overrides.loc !== undefined ? overrides.loc : loc;
      const skipRegistrationDates = hasSubscriptionListFilter({
        ...activeListFiltersRef.current,
        ...overrides,
      });
      const locationParams =
        overrides.locationOverride !== undefined
          ? overrides.locationOverride
          : persistedLocationRef.current || locationFilterParams(effectiveLoc, adminScope, { locked: locationLocked });

      const params = { ...locationParams };
      if (!skipRegistrationDates) {
        const from = formatApiDate(effectiveFromDate);
        const to = formatApiDate(effectiveToDate);
        if (from) params.from = from;
        if (to) params.to = to;
      }
      if (effectiveSearch.trim()) params.search = effectiveSearch.trim();
      return params;
    },
    [fromDate, toDate, searchKey, loc, adminScope, locationLocked]
  );

  const buildListFilters = useCallback(
    (pageOverride, overrides = {}) => {
      const merged = { ...activeListFiltersRef.current, ...overrides };

      const params = {
        page: pageOverride !== undefined ? pageOverride : page,
        no_of_rows: overrides.no_of_rows !== undefined ? overrides.no_of_rows : rowsPerPage,
        ...buildBaseFilters(overrides),
      };

      if (merged.status) params.status = merged.status;
      if (merged.venue_status) params.venue_status = merged.venue_status;
      if (merged.profile) params.profile = merged.profile;
      if (merged.venue_type) params.venue_type = merged.venue_type;
      applySubscriptionParams(params, merged.subscription);
      if (merged.subscription_expiry) params.subscription_expiry = merged.subscription_expiry;
      if (merged.subscription_purchased_today) {
        params.subscription_purchased_today = merged.subscription_purchased_today;
      }

      return params;
    },
    [page, rowsPerPage, buildBaseFilters]
  );

  const loadList = useCallback(
    async (pageOverride, overrides = {}) => {
      const seq = ++listRequestSeq.current;
      setLoading(true);
      setRows([]);

      const mergedFilters = { ...activeListFiltersRef.current, ...overrides };
      const filters = buildListFilters(pageOverride, overrides);
      try {
        const listRes = await getVenues(filters);
        if (seq !== listRequestSeq.current) return;

        const rawRows = Array.isArray(listRes.data?.data) ? listRes.data.data : [];
        const filteredRows = shouldApplyClientListFilter(mergedFilters)
          ? rawRows.filter((row) => matchesListFilters(row, mergedFilters))
          : rawRows;

        setRows(filteredRows);
        setTotalRecords(listRes.data?.totalCount ?? filteredRows.length);
      } catch {
        if (seq !== listRequestSeq.current) return;
        setRows([]);
        setTotalRecords(0);
        toast.info("Venues API unavailable.");
      } finally {
        if (seq === listRequestSeq.current) setLoading(false);
      }
    },
    [buildListFilters]
  );

  const loadAll = useCallback(
    async (pageOverride, overrides = {}) => {
      const seq = ++listRequestSeq.current;
      setLoading(true);
      setRows([]);

      const mergedFilters = { ...activeListFiltersRef.current, ...overrides };
      try {
        const [statsRes, listRes] = await Promise.all([
          getVenueStats(buildBaseFilters(overrides)),
          getVenues(buildListFilters(pageOverride, overrides)),
        ]);
        if (seq !== listRequestSeq.current) return;

        setStats(statsRes.data?.data || null);

        const rawRows = Array.isArray(listRes.data?.data) ? listRes.data.data : [];
        const filteredRows = shouldApplyClientListFilter(mergedFilters)
          ? rawRows.filter((row) => matchesListFilters(row, mergedFilters))
          : rawRows;
        setRows(filteredRows);
        setTotalRecords(listRes.data?.totalCount ?? filteredRows.length);
      } catch {
        if (seq !== listRequestSeq.current) return;
        setStats(null);
        setRows([]);
        setTotalRecords(0);
        toast.info("Venues API unavailable.");
      } finally {
        if (seq === listRequestSeq.current) setLoading(false);
      }
    },
    [buildBaseFilters, buildListFilters]
  );

  const syncFilterState = (filters) => {
    setStatus(filters.status || "");
    setVenueStatus(filters.venue_status || "");
    setSubscription(filters.subscription || "");
    setProfile(filters.profile || "");
    setVenueType(filters.venue_type || "");
  };

  const applyCardFilter = (card) => {
    activeListFiltersRef.current = { ...card.filter };
    syncFilterState(card.filter);
    setSelectedCard(card.key);
    setPage(0);
    loadList(0, activeListFiltersRef.current);
  };

  const handleReset = () => {
    activeListFiltersRef.current = { ...EMPTY_LIST_FILTERS };
    persistedLocationRef.current = null;
    setFromDate(null);
    setToDate(null);
    setSearchKey("");
    syncFilterState(EMPTY_LIST_FILTERS);
    setSelectedCard("total");
    setPage(0);
    if (!locationLocked) {
      loc.resetLocation();
    }
    loadAll(0, {
      ...EMPTY_LIST_FILTERS,
      search: "",
      fromDate: null,
      toDate: null,
      locationOverride: locationLocked
        ? locationFilterParams(loc, adminScope, { locked: true })
        : {},
    });
  };

  useEffect(() => {
    const urlOverrides = getUrlFilterOverrides(searchParams);
    if (hasUrlListFilters(searchParams)) {
      activeListFiltersRef.current = { ...EMPTY_LIST_FILTERS, ...urlOverrides.listFilters };
      persistedLocationRef.current =
        Object.keys(urlOverrides.locationOverride || {}).length > 0
          ? urlOverrides.locationOverride
          : null;
      syncFilterState(urlOverrides.listFilters);
      setFromDate(urlOverrides.fromDate);
      setToDate(urlOverrides.toDate);
      setSearchKey(urlOverrides.search || "");
      setSelectedCard(urlOverrides.selectedCard || "total");
      setPage(0);
      loadAll(0, {
        ...urlOverrides.listFilters,
        search: urlOverrides.search,
        fromDate: urlOverrides.fromDate,
        toDate: urlOverrides.toDate,
        locationOverride: urlOverrides.locationOverride,
      });
    } else {
      activeListFiltersRef.current = { ...EMPTY_LIST_FILTERS };
      persistedLocationRef.current = null;
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearchClick = () => {
    persistedLocationRef.current = null;
    activeListFiltersRef.current = {
      status,
      venue_status: venueStatus,
      subscription,
      profile,
      venue_type: venueType,
      subscription_expiry: activeListFiltersRef.current.subscription_expiry,
      subscription_purchased_today: activeListFiltersRef.current.subscription_purchased_today,
    };
    setFiltersOpen(false);
    setSelectedCard("");
    setPage(0);
    loadAll(0, activeListFiltersRef.current);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    loadList(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    const next = parseInt(e.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
    loadList(0, { no_of_rows: next });
  };

  const handleExport = async () => {
    try {
      await exportVenues(buildListFilters(page));
    } catch {
      toast.warning("Export failed.");
    }
  };

  const typeItems = stats?.items || [];
  const profileBreakdown = stats?.by_profile || {};

  return (
    <Box>
      <SuperAdminPageHeader
        title="Venues"
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset} disabled={loading}>
              Reset
            </Button>
            <Button
              variant={filtersOpen ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((o) => !o)}
            >
              Filters
            </Button>
          </Stack>
        }
      />

      {filtersOpen ? (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack spacing={2.5}>
            <FilterSectionTitle>Date range</FilterSectionTitle>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="Created from"
                  value={fromDate}
                  onChange={setFromDate}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="Created to"
                  value={toDate}
                  onChange={setToDate}
                  format="DD/MM/YYYY"
                  minDate={fromDate?.isValid() ? fromDate : undefined}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
            </Grid>

            <Divider />

            <FilterSectionTitle>Location</FilterSectionTitle>
            <Grid container spacing={2}>
              <SuperAdminLocationSelects
                {...loc}
                locationLocked={locationLocked}
                lockedScope={adminScope}
                menuMinWidth={280}
                itemProps={FILTER_ITEM}
              />
            </Grid>

            <Divider />

            <FilterSectionTitle>Status & profile</FilterSectionTitle>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="venue-filter-manager-status"
                  label="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  options={STATUS_OPTIONS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="venue-filter-venue-status"
                  label="Venue status"
                  value={venueStatus}
                  onChange={(e) => setVenueStatus(e.target.value)}
                  options={VENUE_STATUS_OPTIONS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="venue-filter-subscription"
                  label="Subscription"
                  value={subscription}
                  onChange={(e) => setSubscription(e.target.value)}
                  options={SUBSCRIPTION_OPTIONS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="venue-filter-profile"
                  label="Profile"
                  value={profile}
                  onChange={(e) => setProfile(e.target.value)}
                  options={PROFILE_OPTIONS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="venue-filter-venue-type"
                  label="Venue type"
                  value={venueType}
                  onChange={(e) => setVenueType(e.target.value)}
                  options={VENUE_TYPE_OPTIONS}
                />
              </Grid>
            </Grid>

            <Divider />

            <FilterSectionTitle>Search</FilterSectionTitle>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Manager name, email, mobile, or venue name"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button variant="contained" onClick={handleSearchClick} disabled={loading} fullWidth>
                    {loading ? <CircularProgress size={22} color="inherit" /> : "Search"}
                  </Button>
                  <Button variant="outlined" onClick={handleExport} disabled={loading} fullWidth>
                    Export Data
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      {stats ? (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {STAT_CARDS.map((card) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={card.key}>
                <Paper
                  role="button"
                  tabIndex={0}
                  onClick={() => applyCardFilter(card)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      applyCardFilter(card);
                    }
                  }}
                  sx={{
                    ...cardPaperSx(selectedCard === card.key),
                    bgcolor: "secondary.main",
                    color: "secondary.contrastText",
                  }}
                >
                  <Typography variant="body2">{card.label}</Typography>
                  <Typography variant="statValue" sx={{ fontWeight: 700 }}>
                    {stats[card.key] ?? 0}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {Object.keys(profileBreakdown).length > 0 ? (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {PROFILE_CARD_KEYS.map((tier) => (
                <Grid item xs={4} sm={4} md={2} key={tier}>
                  <Paper
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      applyCardFilter({
                        key: tier,
                        label: tier,
                        filter: { ...EMPTY_LIST_FILTERS, profile: tier.toLowerCase() },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        applyCardFilter({
                          key: tier,
                          label: tier,
                          filter: { ...EMPTY_LIST_FILTERS, profile: tier.toLowerCase() },
                        });
                      }
                    }}
                    sx={{
                      ...cardPaperSx(selectedCard === tier),
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    }}
                  >
                    <Typography variant="body2">{tier}</Typography>
                    <Typography variant="statValue" sx={{ fontWeight: 700 }}>
                      {profileBreakdown[tier] ?? 0}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : null}

          {typeItems.length > 0 ? (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {typeItems.map((item) => {
                const cardKey = `type:${item.venue_type}`;
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.venue_type}>
                    <Paper
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        applyCardFilter({
                          key: cardKey,
                          label: item.venue_type,
                          filter: { ...EMPTY_LIST_FILTERS, venue_type: item.venue_type },
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          applyCardFilter({
                            key: cardKey,
                            label: item.venue_type,
                            filter: { ...EMPTY_LIST_FILTERS, venue_type: item.venue_type },
                          });
                        }
                      }}
                      sx={{
                        ...cardPaperSx(selectedCard === cardKey),
                        bgcolor: "primary.dark",
                        color: "primary.contrastText",
                      }}
                    >
                      <Typography variant="body2">{item.venue_type}</Typography>
                      <Typography variant="statValue" sx={{ fontWeight: 700 }}>
                        {item.count ?? 0}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          ) : null}
        </>
      ) : null}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main", mb: 1.5 }}>
          {getTableTitle(selectedCard, typeItems)}
          {!loading && totalRecords > 0 ? (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({totalRecords} {totalRecords === 1 ? "record" : "records"})
            </Typography>
          ) : null}
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.main" }}>
                {TABLE_HEADERS.map((h) => (
                  <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} aria-label="Loading venues" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No venues for the current criteria.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => (
                  <TableRow
                    key={`${row.venue_id ?? "row"}-${idx}`}
                    hover
                    onClick={() => row.venue_id && navigate(`/superAdmin/venues/${row.venue_id}`)}
                    sx={{ cursor: row.venue_id ? "pointer" : "default" }}
                  >
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>{row.venue_manager_name ?? row.name ?? "—"}</TableCell>
                    <TableCell>{row.contact_number ?? row.mobile_no ?? "—"}</TableCell>
                    <TableCell>
                      {row.venue_id ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/superAdmin/venues/${row.venue_id}`);
                          }}
                          sx={{ color: "primary.main", fontWeight: 700, textTransform: "none", px: 0.5, minWidth: 0, justifyContent: "flex-start" }}
                        >
                          {row.venue_name}
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <ProfileBadge profile={row.profile} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.status ?? row.account_status} />
                    </TableCell>
                    <TableCell>
                      <SubscriptionIndicator
                        hasSubscription={row.has_subscription}
                        isActive={row.is_subscription_active}
                      />
                    </TableCell>
                    <TableCell>{row.city_name ?? "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDisplayDateTime(row.created_at)}
                    </TableCell>
                    <TableCell align="center">
                      <SuperAdminViewAction
                        onClick={(event) => {
                          event.stopPropagation();
                          if (row.venue_id) navigate(`/superAdmin/venues/${row.venue_id}`);
                        }}
                        disabled={!row.venue_id}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  disabled={loading}
                  colSpan={TABLE_HEADERS.length}
                  sx={{ border: 0 }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
